import { createEcsWebClient } from "./ecsWebClient";

export type CloudBaseWebConfig = {
  envId: string;
  region: string;
  publishableKey: string;
};

export type CloudBaseUser = {
  id?: unknown;
  email?: unknown;
  phone?: unknown;
  is_anonymous?: boolean;
  user_metadata?: Record<string, unknown>;
};

export type CloudBaseSession = {
  access_token: string;
  user: CloudBaseUser;
};

export type CloudBaseAuthResult = {
  data?: { session?: CloudBaseSession | null } | null;
  error?: unknown;
};

export interface CloudBaseAuthClient {
  getSession(): Promise<CloudBaseAuthResult>;
  signInWithPassword(input: { username: string; password: string }): Promise<CloudBaseAuthResult>;
  signOut(): Promise<unknown>;
}

export type CloudBaseRdbError = {
  code?: string;
  message?: string;
  details?: string;
};

export type CloudBaseRdbResponse = {
  data?: unknown;
  error?: CloudBaseRdbError | null;
};

export interface CloudBaseRdbClient {
  rpc(name: string, parameters?: Record<string, unknown>): PromiseLike<CloudBaseRdbResponse>;
}

export interface CloudBaseWebClient {
  auth: CloudBaseAuthClient;
  rdb(options: { instance: string; schema: "public" }): CloudBaseRdbClient;
  callFunction<T>(options: {
    name: string;
    data: Record<string, unknown>;
    parse: true;
  }): Promise<{ requestId: string; result: T }>;
}

const clients = new Map<string, CloudBaseWebClient>();

function cacheKey(config: CloudBaseWebConfig): string {
  return `${config.envId}\u0000${config.region}\u0000${config.publishableKey}`;
}

export function getCloudBaseWebClient(config: CloudBaseWebConfig): CloudBaseWebClient {
  const key = cacheKey(config);
  const cached = clients.get(key);
  if (cached) return cached;

  // Keep the existing module interface while using ECS for every transport.
  const client = createEcsWebClient(import.meta.env.VITE_ECS_API_BASE_URL?.trim() || "/ecs");
  clients.set(key, client);
  return client;
}
